import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { SavedProgramParseError, upsertProgram } from '../api/programForm.api';
import { ProgramUpsertRequest } from '../model/programForm.mapper';
import { SavedProgram } from '../api/programForm.response';
import { ProgramFormValues } from '../model/programForm.types';

export const useUpsertProgram = (
  buildPayload: (values: ProgramFormValues) => ProgramUpsertRequest,
  onSaved: (saved: SavedProgram) => void,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlight = useRef(false);
  const buildPayloadRef = useRef(buildPayload);
  const onSavedRef = useRef(onSaved);
  buildPayloadRef.current = buildPayload;
  onSavedRef.current = onSaved;

  const handleSave = useCallback(
    async (values: ProgramFormValues): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setIsSubmitting(true);
      let lockForm = false;
      let saved: SavedProgram | null = null;
      try {
        saved = await upsertProgram(buildPayloadRef.current(values));
        toast.success('Saved');
      } catch (error) {
        if (error instanceof SavedProgramParseError) {
          // Save succeeded but the response shape is unexpected. Keep the
          // form locked (isSubmitting stays true) so the user cannot click
          // Save again and create a duplicate program; the toast directs
          // them to reload.
          console.error('Program saved but response parse failed', error);
          toast.error('Saved, but could not refresh. Reload the page.');
          lockForm = true;
        } else {
          console.error('Upsert program failed', error);
          toast.error('Failed to save. Please try again.');
        }
      } finally {
        inFlight.current = false;
        if (!lockForm) setIsSubmitting(false);
      }
      // Notify the consumer outside the try block: an exception thrown by
      // their callback (e.g., a refetch failure) must not be mistaken for a
      // save failure and trigger the "Failed to save" toast.
      if (saved) {
        onSavedRef.current(saved);
        return true;
      }
      return false;
    },
    [],
  );

  return { isSubmitting, handleSave };
};
